import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { useCompany } from "#modules/company/view/useCompany";
import { isOptimizationInvalidationCancelled } from "#modules/optimizations/infra/optimizationInvalidationConfirmation";
import {
  invalidatePart,
  markQuoteRequestLinesPredictingForPart,
} from "#modules/parts/infra/invalidatePart";
import { updatePart } from "#modules/parts/infra/updatePart.api";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";
import { JobStatusSchema } from "shared";

type Options = {
  onSuccess?: () => void;
};

export const useUpdatePart = (partId?: string, options?: Options) => {
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const { company } = useCompany();

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: updatePart,
    onSuccess: (_data, variables) => {
      if (partId) {
        if (variables.payload.formattedFeaturesJsonOverride !== undefined) {
          const quoteRequestLines = markQuoteRequestLinesPredictingForPart(queryClient, partId);

          quoteRequestLines.forEach((quoteRequestLine) => {
            if (quoteRequestLine.predictionStatus === JobStatusSchema.enum.PROCESSED) {
              analytics.capture(AnalyticsEvent.PredictionRerun, {
                quote_request_line_id: quoteRequestLine.id,
                trigger: "updatePartFeatures",
                company_id: company?.id ?? null,
                company_name: company?.name ?? null,
              });
            }
          });

          void queryClient.invalidateQueries({ queryKey: queryKeys.parts.features(partId) });
        }

        void invalidatePart(queryClient, { partId });
      }
      options?.onSuccess?.();
    },
    onError: (err: Error) => {
      if (isOptimizationInvalidationCancelled(err)) {
        return;
      }

      showErrorToast({ title: err.message });
      errorHandler(err);
    },
  });

  return { updatePart: mutate, updatePartAsync: mutateAsync, ...rest };
};
